import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'

// ─── Constants ────────────────────────────────────────────────────────────────

const COMMON_STORES = [
  'Walmart', 'Target', 'Costco', 'Whole Foods', "Trader Joe's",
  'Kroger', 'Safeway', 'Publix', 'Aldi', 'Lidl',
  "Sam's Club", 'HEB', 'Wegmans',
]

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  value:        string
  onChange:     (store: string) => void
  householdId:  string
  placeholder?: string
  className?:   string
}

// ─── StoreInput ───────────────────────────────────────────────────────────────

export default function StoreInput({ value, onChange, householdId, placeholder = 'Store (optional)', className = '' }: Props) {
  const [focused,    setFocused]    = useState(false)
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data: userStores = [] } = useQuery({
    queryKey: ['user-stores', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('user_stores')
        .select('store_name, visit_count, is_favorite')
        .eq('household_id', householdId)
        .order('visit_count', { ascending: false })
        .limit(10)
      return data ?? []
    },
  })

  // What to show in the dropdown
  const hasHistory    = userStores.length > 0
  const inputLower    = value.toLowerCase()
  const showDropdown  = focused

  let dropdownItems: { name: string; visitCount?: number; isFavorite?: boolean }[]

  if (hasHistory) {
    dropdownItems = userStores
      .filter(s => !value || s.store_name.toLowerCase().includes(inputLower))
      .map(s => ({ name: s.store_name, visitCount: s.visit_count, isFavorite: s.is_favorite }))
  } else {
    dropdownItems = COMMON_STORES
      .filter(s => !value || s.toLowerCase().includes(inputLower))
      .map(name => ({ name }))
  }

  function handleFocus() {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current)
    setFocused(true)
  }

  function handleBlur() {
    // Delay so click on dropdown option registers before hiding
    blurTimerRef.current = setTimeout(() => setFocused(false), 150)
  }

  function handleSelect(name: string) {
    onChange(name)
    setFocused(false)
  }

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="w-full rounded-[14px] border border-border-sand bg-paper px-4 py-3 font-serif text-[15px] text-ink placeholder:text-ink-muted placeholder:italic focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
      />

      {showDropdown && dropdownItems.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-[14px] border border-border-sand bg-paper shadow-md overflow-hidden">
          {dropdownItems.slice(0, 6).map(item => (
            <button
              key={item.name}
              type="button"
              onMouseDown={() => handleSelect(item.name)}
              className="w-full flex items-center justify-between px-4 py-2.5 font-serif text-[14px] text-ink hover:bg-cream-warm active:bg-cream-warm cursor-pointer border-b border-border-hairline last:border-b-0 text-left"
            >
              <span>
                {item.isFavorite ? '⭐ ' : ''}{item.name}
              </span>
              {item.visitCount !== undefined && item.visitCount > 0 && (
                <span className="text-[11px] italic text-ink-muted">
                  {item.visitCount}×
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
